import type { ImportKeyKindValue } from './importMappings';
import type { CompanyMailboxKindValue } from '../records/companyMailbox';
import { comparablePhone, normaliseCompanyName } from '../records/normalise';
import { t } from '../strings';
import type { BooksAddress, BooksContact, BooksContactPerson } from './booksSource';
import type { StagedStudyView } from './importStudy';
import type { ImportPassesSummary } from './importSteps';
import { IMPORT_SCOPE_KINDS, type ImportRunView, type ImportScopeKindValue, type ImportWaitsOnRow, type OpeningUnlinkedAccount, type PlaceholderSiteOpened } from './importRun';

/**
 * The staged import (piece 31; decisions 1055, 1483): every run reads its
 * scope once into one `ImportStagedRow` per top-level source record and writes
 * nothing live. Write all then accepts every undecided row from those rows, or
 * a person decides each row, one at a time or a selection in bulk, and an
 * Accept writes that row at once through the block's own writer.
 */

/**
 * 🔒 The kinds a staged row names (decision 1483): a scope's kinds, a documents run's opening balances, staged as its
 * first row, and the invoices and bills open at the cut-off, staged as its second (decision 2991).
 */
/** 🔒 Decision 12660 adds `LookupItem`, a lookup run's row, which no run's scope names. */
export const IMPORT_STAGED_KINDS = [...IMPORT_SCOPE_KINDS, 'OpeningBalance', 'OpenDocuments', 'LookupItem'] as const;
export type ImportStagedKindValue = (typeof IMPORT_STAGED_KINDS)[number];

export const IMPORT_MATCH_GRADES = ['Exact', 'Weak', 'NoMatch'] as const;
export type ImportMatchGradeValue = (typeof IMPORT_MATCH_GRADES)[number];

export const IMPORT_STAGED_OUTCOMES = ['Create', 'Update', 'Unchanged', 'Reject'] as const;
export type ImportStagedOutcomeValue = (typeof IMPORT_STAGED_OUTCOMES)[number];

export const IMPORT_STAGED_DECISIONS = ['Accept', 'Skip', 'Edit', 'Merge'] as const;
export type ImportStagedDecisionValue = (typeof IMPORT_STAGED_DECISIONS)[number];

export const IMPORT_MATCHED_RECORD_TYPES = ['Account', 'SalesTaxRate', 'LocalTaxJurisdiction', 'CompanyRecord', 'PersonRecord', 'LocationRecord', 'Item', 'Invoice', 'CreditNote', 'Payment', 'Bill', 'VendorCredit', 'JournalEntry', 'Quote', 'SalesOrder', 'PurchaseOrder', 'ExpenseReport'] as const;
export type ImportMatchedRecordTypeValue = (typeof IMPORT_MATCHED_RECORD_TYPES)[number];

export const IMPORT_MERGE_TARGET_TYPES = ['Account', 'SalesTaxRate', 'LocalTaxJurisdiction', 'CompanyRecord', 'Item'] as const;
export type ImportMergeTargetTypeValue = (typeof IMPORT_MERGE_TARGET_TYPES)[number];

/** The block 4 document kinds, whose staged rows offer Accept and Skip only; the opening balances offer the same two (decision 1483), and so do block 5's bank lines (decision 8040). */
export const DOCUMENT_SCOPE_KINDS: readonly ImportScopeKindValue[] = ['Estimate', 'SalesOrder', 'Invoice', 'CreditNote', 'CustomerPayment', 'PurchaseOrder', 'Bill', 'VendorCredit', 'VendorPayment', 'Expense', 'ManualJournal', 'InventoryAdjustment', 'BankTransaction'];

/**
 * 🔒 Two rows are the same shape when they share the source kind, the proposed
 * outcome, the match rule and grade, and the set of fields whose values differ
 * (decision 1055). A shape is compared within one run and never carried on.
 */
export function shapeKeyOf(input: { sourceKind: string; proposedOutcome: string; matchRuleId: string | null; matchGrade: string; differingFields: readonly string[] }): string {
  return [input.sourceKind, input.proposedOutcome, input.matchRuleId ?? 'reference', input.matchGrade, [...input.differingFields].sort().join('+')].join('|');
}

/** The refusal on a row whose contact person gives one name and waits on an answer (decision 2500). */
export const ONE_NAME_WAITING = 'A contact person on this row gives one name; answer who they are first: type the other name, pick the person here, or skip them.';

/** 🔒 The refusal on a row carrying a same-person pair nobody has answered (decision 2970). */
/**
 * 🔒 A domain match whose folded names differ is graded Weak, and its reason
 * names both names (decision 7880): the clause the reason closes on.
 */
export function namesDifferNote(incoming: string, here: string): string {
  return `the names differ (${incoming}, ${here}): check before Accept`;
}

/** 🔒 Whether a staged row's proposed match is Weak with differing names (decision 7880), which a remembered Accept passes over. */
export function namesDiffer(row: { matchGrade: string | null; matchReason: string | null }): boolean {
  return row.matchGrade === 'Weak' && (row.matchReason ?? '').includes('; the names differ (');
}

export const SAME_PERSON_WAITING = 'Answer the same-person question first: Merge or Keep both.';

/** 🔒 The words an obsolete item's proposal carries in its reason (decision 8260), by which a remembered Accept passes over it. */
export const OBSOLETE_ITEM_MARK = 'proposes Merge into the item that replaced it';

/** 🔒 The key an obsolete item matched its replacement on (decision 8260). */
export type ObsoleteItemKey = 'name' | 'SKU' | 'part number';

/** 🔒 An obsolete item's reason (decision 8260): both items by name, the key they share, and the proposal. */
export function obsoleteItemReason(obsolete: string, current: string, key: ObsoleteItemKey): string {
  return `Zoho marks ${obsolete} inactive, and ${current} carries the same ${key}${key === 'name' ? ' (the only thing that matched, so check it is the same part)' : ''}, so it ${OBSOLETE_ITEM_MARK}; Accept links the old Zoho item to it, and Not This One keeps them apart.`;
}

/** 🔒 Whether a staged row is an obsolete item's Weak proposal (decision 8260), which Write all leaves and a remembered Accept never sweeps. */
export function isObsoleteItemProposal(row: { sourceKind: string; matchGrade: string | null; matchReason: string | null }): boolean {
  return row.sourceKind === 'Item' && row.matchGrade === 'Weak' && (row.matchReason ?? '').includes(OBSOLETE_ITEM_MARK);
}

/** 🔒 The fold an obsolete item's keys are compared by (decision 8260): accents, case and everything but letters and digits dropped. */
export function obsoleteItemFold(value: string | null | undefined): string {
  return (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** 🔒 How many of a bulk Accept's refusals wait on a same-person question (decision 2970), which the notice counts once instead of repeating. */
export function samePersonWaitingCount(refused: readonly { reason: string }[]): number {
  return refused.filter((one) => one.reason === SAME_PERSON_WAITING).length;
}

/**
 * 🔒 A person's answer to a contact person's one-name question (decision
 * 2500): the missing name typed, the person here they are, or skip. Kept on
 * the run by the Zoho contact person's id.
 */
export const CONTACT_PERSON_ANSWER_KINDS = ['Name', 'Person', 'Skip', 'SameAs', 'KeepBoth', 'Mailbox'] as const;
export type ContactPersonAnswerKind = (typeof CONTACT_PERSON_ANSWER_KINDS)[number];
/** 🔒 SameAs and KeepBoth answer a possible pair (decision 2502): the twin is the other contact person, by Zoho id. */
export type ContactPersonAnswer = (
  | { kind: 'Name'; firstName: string; lastName: string }
  | { kind: 'Person'; personId: string; label: string }
  | { kind: 'Skip' }
  /** A pair answer stored before decision 3080, naming one twin; read as a pair map of one. */
  | { kind: 'SameAs'; twinContactPersonId: string; twinLabel: string }
  | { kind: 'KeepBoth'; twinContactPersonId: string }
  /** 🔒 The person's pair answers, one per twin, keyed by the twin's Zoho id (decision 3080). */
  | { kind: 'Pairs'; pairs: Record<string, SamePersonPairAnswer> }
  /** 🔒 The address is the company's mailbox of a kind, not a person (decision 2973); the Accept writes it on the company. */
  | { kind: 'Mailbox'; mailboxKind: CompanyMailboxKindValue; email: string }
) & {
  /** Who gave the answer and when, written with it (T1811); an answer from before this carries neither. */
  answeredByPersonId?: string;
  answeredAt?: string;
};

/** 🔒 One pair's answer, kept per pair (decision 3080): Merge or Keep both, with the twin's label and who answered. */
export interface SamePersonPairAnswer {
  kind: 'SameAs' | 'KeepBoth';
  /** The twin as a row's line names them, *Mike Toth at Jeranna Inc*. */
  twinLabel: string;
  answeredByPersonId?: string;
  answeredAt?: string;
}

/**
 * 🔒 A person's pair answers, by twin (decision 3080). A stored answer from
 * before the decision names one twin and reads as a map of one; any other
 * answer holds no pair.
 */
export function pairAnswersOf(answer: ContactPersonAnswer | null | undefined): Record<string, SamePersonPairAnswer> {
  if (!answer) return {};
  const by = { ...(answer.answeredByPersonId ? { answeredByPersonId: answer.answeredByPersonId } : {}), ...(answer.answeredAt ? { answeredAt: answer.answeredAt } : {}) };
  if (answer.kind === 'Pairs') return { ...answer.pairs };
  if (answer.kind === 'SameAs') return { [answer.twinContactPersonId]: { kind: 'SameAs', twinLabel: answer.twinLabel, ...by } };
  if (answer.kind === 'KeepBoth') return { [answer.twinContactPersonId]: { kind: 'KeepBoth', twinLabel: '', ...by } };
  return {};
}

/** 🔒 The answer to the pair of two contact people (decision 3080), read from either side, or null where neither holds one. */
export function pairAnswerOf(answers: Readonly<Record<string, ContactPersonAnswer>>, contactPersonId: string, twinContactPersonId: string): 'SameAs' | 'KeepBoth' | null {
  return pairAnswersOf(answers[contactPersonId])[twinContactPersonId]?.kind ?? pairAnswersOf(answers[twinContactPersonId])[contactPersonId]?.kind ?? null;
}

/**
 * 🔒 Everybody one person's Merge answers reach (decision 3080), the person
 * left out, each with the label a pair answer gives them. A Keep both answer
 * joins nobody; a path of Merge answers joins two people however long it is.
 */
export function samePersonGroup(answers: Readonly<Record<string, ContactPersonAnswer>>, contactPersonId: string): { contactPersonId: string; label: string }[] {
  const merged = new Map<string, Map<string, string>>();
  const join = (one: string, other: string, label: string) => {
    const edges = merged.get(one) ?? new Map<string, string>();
    if (!edges.has(other) || (!edges.get(other) && label)) edges.set(other, label);
    merged.set(one, edges);
  };
  for (const [id, answer] of Object.entries(answers)) {
    for (const [twin, pair] of Object.entries(pairAnswersOf(answer))) {
      if (pair.kind !== 'SameAs') continue;
      join(id, twin, pair.twinLabel);
      join(twin, id, '');
    }
  }
  const labels = new Map<string, string>();
  for (const edges of merged.values()) for (const [id, label] of edges) if (label && !labels.has(id)) labels.set(id, label);
  const seen = new Set([contactPersonId]);
  const queue = [contactPersonId];
  const out: { contactPersonId: string; label: string }[] = [];
  while (queue.length > 0) {
    const next = queue.shift() as string;
    for (const other of [...(merged.get(next)?.keys() ?? [])].sort()) {
      if (seen.has(other)) continue;
      seen.add(other);
      queue.push(other);
      out.push({ contactPersonId: other, label: labels.get(other) ?? '' });
    }
  }
  return out;
}

/** 🔒 One of a contact row's people whose name another contact's person shares (decision 2502), with the pair's answer or none. */
export interface SamePersonPair {
  contactPersonId: string;
  name: string;
  twinContactPersonId: string;
  twinName: string;
  twinCompany: string;
  answer: 'SameAs' | 'KeepBoth' | null;
  /** 🔒 This row's person and their contact as the window draws them (decision 3083); absent on a run staged before. */
  mine?: SamePersonSide;
  /** 🔒 The twin and their contact, the same way. */
  twin?: SamePersonSide;
}

/**
 * 🔒 One side of a same-person pair (decision 3083): the person's own fields
 * from Zoho Books and their contact's, which the window draws in a column.
 */
export interface SamePersonSide {
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  isPrimary: boolean;
  /** The contact's own name in Zoho Books. */
  contactName: string;
  /** The contact's company name, or null on a contact that carries none, which is the person's own contact. */
  companyName: string | null;
  isCustomer: boolean;
  isVendor: boolean;
  contactPhone: string | null;
  /** The contact's first address on one line, the billing address before the shipping one. */
  address: string | null;
}

/** An address on one line, its parts in order and the blanks left out. */
function addressLine(address: BooksAddress | null): string | null {
  if (!address) return null;
  const parts = [...address.lines, address.city, [address.state, address.postalCode].filter(Boolean).join(' '), address.country].map((part) => (part ?? '').trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** 🔒 A pair side read off a pulled contact and one of its people (decision 3083). */
export function samePersonSide(contact: BooksContact, person: BooksContactPerson): SamePersonSide {
  const companyName = (contact.companyName ?? '').trim();
  return {
    name: [person.firstName, person.lastName].filter(Boolean).join(' '),
    title: person.title,
    email: person.email,
    phone: person.phone,
    mobile: person.mobile,
    isPrimary: person.isPrimary,
    contactName: contact.name,
    companyName: companyName || null,
    isCustomer: contact.isCustomer,
    isVendor: contact.isVendor,
    contactPhone: contact.phone,
    address: addressLine(contact.billingAddress) ?? addressLine(contact.shippingAddress),
  };
}

/** The lines the window draws for a pair, in order: each names a field of the side. */
export const SAME_PERSON_LINES = ['title', 'email', 'phone', 'mobile', 'isPrimary', 'contactName', 'companyName', 'kind', 'contactPhone', 'address'] as const;
export type SamePersonLine = (typeof SAME_PERSON_LINES)[number];

/** What a side reads on one line, as text or null for a blank; the yes, no and kind words are the screen's to name. */
export function samePersonLineValue(side: SamePersonSide, key: SamePersonLine): string | boolean | null {
  if (key === 'isPrimary') return side.isPrimary;
  if (key === 'kind') return side.isCustomer && side.isVendor ? 'CustomerAndVendor' : side.isCustomer ? 'Customer' : side.isVendor ? 'Vendor' : null;
  const value = side[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** 🔒 Whether the two sides hold one line differently (decision 3083): text is compared without case or spacing, and two blanks agree. */
export function samePersonLineDiffers(mine: SamePersonSide, twin: SamePersonSide, key: SamePersonLine): boolean {
  // 🔒 A phone is compared as the number it dials (T2253), so (412) 555-0100 and +14125550100 agree, as the duplicate check reads them.
  const fold = (value: string | boolean | null) => (typeof value === 'string' ? (key === 'phone' || key === 'mobile' || key === 'contactPhone' ? comparablePhone(value) || value.toLowerCase() : value.toLowerCase().replace(/\s+/g, ' ')) : value);
  return fold(samePersonLineValue(mine, key)) !== fold(samePersonLineValue(twin, key));
}

/**
 * 🔒 How a pair's answer names a person (T2253): *Mike Toth at Toth Twins*, or *Mike Toth on their own contact* where the
 * contact carries no company name, never *at* themselves. A run staged before the sides were kept names the company it stored.
 */
export function samePersonLabel(name: string, side: SamePersonSide | undefined, company: string): string {
  if (!side) return `${name} at ${company}`;
  return side.companyName ? `${name} at ${side.companyName}` : `${name} ${t('import.samePerson.onOwnContact')}`;
}

/** One contact person on a staged contact row who gives one name, with the answer given or none. */
export interface OneNameQuestion {
  contactPersonId: string;
  /** The one name Zoho Books gives. */
  given: string;
  email: string | null;
  /** The contact the person travels in, as the row names it. */
  company: string;
  answer: ContactPersonAnswer | null;
  /**
   * 🔒 The name the read proposed off the email where Zoho Books gives none (decision 6330): both names, which the
   * row's Accept writes unless an answer replaces it, or a first name alone, which only fills the window.
   */
  proposed?: { firstName: string; lastName: string } | null;
}

/** What a person sends at a one-name question. */
export interface ContactPersonAnswerBody {
  contactPersonId: string;
  answer: { kind: 'Name'; firstName: string; lastName: string } | { kind: 'Person'; personId: string } | { kind: 'Skip' } | { kind: 'SameAs' | 'KeepBoth'; twinContactPersonId: string } | { kind: 'Mailbox'; mailboxKind: CompanyMailboxKindValue };
}

/** A person here a one-name question's Pick can name, found by what a person typed. */
export interface OneNamePersonOption {
  personId: string;
  label: string;
}

/**
 * 🔒 A person this run typed on another row's one-name question and has not yet
 * written (decision 3161; T2240): listed grayed under Pick the person here, and
 * never picked, because an answer names a person by id and this one has none.
 */
export interface OneNameUnwrittenPerson {
  label: string;
  /** The row the person was typed on, which writes them once accepted. */
  sequence: number;
}

/** 🔒 What Pick the person here's Find answers (decisions 2500, 3161): the people here, and the people this run typed but has not yet written. */
export interface OneNamePersonSearch {
  candidates: OneNamePersonOption[];
  unwritten: OneNameUnwrittenPerson[];
}

/** Whether a decision may be made on a row, as the refusal a person reads, or null. */
export interface StagedOffers {
  accept: string | null;
  skip: string | null;
  edit: string | null;
  merge: string | null;
  notThisOne: string | null;
}

/** 🔒 Why Not this one is shut on a row an earlier pull linked (decision 7410): there is no proposed match to set aside. */
export const LINKED_NOT_THIS_ONE = 'An earlier pull linked this row to the record here, so there is no proposed match to set aside; Skip keeps the record here, or Merge writes this row onto the record you pick.';

/** 🔒 Why Accept is shut on a row whose record was changed here (decisions 0921, 7410); Step through reads it to draw the ways forward beside it. */
export const CHANGED_HERE_ACCEPT = 'The record here was changed since the last pull; merge to keep the change, or start a run with Overwrite local changes ticked.';

/**
 * 🔒 What a staged row offers (decision 1055). A decided row offers nothing
 * until it is reopened. A block 4 document offers Accept and Skip only. A tax
 * with no match offers Skip and Merge, never Accept, because a tax is placed
 * and never created. A row whose record changed here offers Accept only when
 * the run ticked Overwrite local changes. A row the proposal rejects offers no
 * Accept, and a row with no match has nothing to set aside. A row an earlier
 * pull linked by its reference has no proposed match either (decision 7410).
 */
export function stagedOffers(row: {
  sourceKind: ImportStagedKindValue; decided: boolean; proposedOutcome: ImportStagedOutcomeValue; proposedReason: string | null; matched: boolean; matchSetAside: boolean; changedHere: boolean; unitMissing: boolean;
  /** 🔒 The match is the link an earlier pull left, not a rule's proposal (decision 7410). */
  linked?: boolean;
  /** 🔒 A contact person on the row gives one name and waits on a person's answer (decision 2500). */
  askWaiting?: boolean;
  /** 🔒 A same-person pair on the row has no answer yet (decision 2970). */
  pairWaiting?: boolean;
}, run: { overwriteLocalChanges: boolean; open: boolean }): StagedOffers {
  const closed = !run.open ? 'The run is closed; revisit the row from the cleanup list to decide it again.' : row.decided ? 'The row is decided; reopen it to decide it again.' : null;
  if (closed) return { accept: closed, skip: closed, edit: closed, merge: closed, notThisOne: closed };
  const noMatch = !row.matched || row.matchSetAside ? 'The row proposes no match to set aside.' : row.linked ? LINKED_NOT_THIS_ONE : null;
  // 🔨 A row waiting on a one-name question writes nothing until it is answered (decision 2500); Skip and Not this one stay open.
  if (row.askWaiting) return { accept: ONE_NAME_WAITING, skip: null, edit: ONE_NAME_WAITING, merge: ONE_NAME_WAITING, notThisOne: noMatch };
  if (row.pairWaiting) return { accept: SAME_PERSON_WAITING, skip: null, edit: SAME_PERSON_WAITING, merge: SAME_PERSON_WAITING, notThisOne: noMatch };
  const document = row.sourceKind === 'OpeningBalance' || row.sourceKind === 'OpenDocuments' || (DOCUMENT_SCOPE_KINDS as readonly string[]).includes(row.sourceKind);
  const tax = row.sourceKind === 'Tax';
  const documentOnly = 'A document becomes what Zoho Books posted, so it is accepted or skipped as it stands.';
  const accept =
    row.unitMissing ? 'Match its unit first, and the row converts again.'
    : tax && !row.matched ? 'A tax is placed by a person and never created; merge it into a tax row here.'
    : row.changedHere && !run.overwriteLocalChanges ? CHANGED_HERE_ACCEPT
    : row.proposedOutcome === 'Reject' ? (row.proposedReason ? `This row would be rejected: ${row.proposedReason}` : 'This row would be rejected, so there is nothing to accept.')
    : null;
  return {
    accept,
    skip: null,
    // An Edit may mend what a proposal rejects, a missing SKU among them, so it is refused only where Accept is for another reason.
    edit: document ? documentOnly
      : tax ? 'A tax takes no values of its own here; merge it into a tax row here.'
      : row.unitMissing ? 'Match its unit first, and the row converts again.'
      : row.changedHere && !run.overwriteLocalChanges ? CHANGED_HERE_ACCEPT
      : null,
    merge: document ? documentOnly : null,
    notThisOne: document ? documentOnly : noMatch ? noMatch : tax ? 'A tax is never created, so a proposed tax row is merged or skipped.' : null,
  };
}

/**
 * 🔒 Decision 8160 (T3176): whether a row proposes a match Not This One can set aside. It carries a record here, or a
 * rule's join to the company a sibling row of the read creates, which has no record until that row is written. A row
 * set aside proposes none.
 */
export function proposesMatch(row: { matchedRecordId: string | null; matchSetAside: boolean; joinsSibling: boolean }): boolean {
  return !row.matchSetAside && (row.matchedRecordId !== null || row.joinsSibling);
}

/** One field of the compare: the record here as it now is beside the converted incoming value. */
export interface StagedFieldView {
  field: string;
  here: string | null;
  converted: string | null;
  /** What the person typed, where the row was accepted with edits. */
  edited: string | null;
  differs: boolean;
  /** The conversion's notes about this field. */
  notes: string[];
  /** How Edit offers the field: typed as text or as a number, or not offered. */
  editable: 'text' | 'number' | 'none';
  /** 🔒 Whether a Merge carries the field and Step through's Accept draws its box (decision 3084); absent on a field neither carries. */
  pickable?: boolean;
}

/**
 * 🔒 The fields a new record is made with (decision 3084): on a row with no
 * record here their boxes stay ticked and locked, since the create cannot
 * leave them blank. A kind not named here draws no box.
 */
export const ACCEPT_LOCKED_ON_CREATE: Readonly<Partial<Record<ImportStagedKindValue, readonly string[]>>> = {
  Contact: ['displayName'],
  Item: ['sku', 'manufacturerPartNumber', 'name', 'unitId'],
  Account: ['accountNumber', 'name', 'cashFlowSection'],
};

/** What Step through's Accept column says a field does (decision 3084). */
export const ACCEPT_WORDS = ['Update', 'Create', 'Keep', 'Same'] as const;
export type AcceptWord = (typeof ACCEPT_WORDS)[number];

/** Whether a staged row writes into a record here: it carries a match nobody set aside. */
export function acceptHasRecord(row: Pick<StagedRowView, 'matched' | 'matchSetAside' | 'linked'>): boolean {
  // 🔒 Decision 7410: a row an earlier pull linked writes into that record whatever the flag says, so it never reads Create.
  return row.matched !== null && (!row.matchSetAside || row.linked === true);
}

/** Whether a field's box is locked ticked: a field the new record is made with, on a row with no record here. */
export function acceptLocked(kind: ImportStagedKindValue, hasRecord: boolean, field: string): boolean {
  return !hasRecord && (ACCEPT_LOCKED_ON_CREATE[kind] ?? []).includes(field);
}

const blankValue = (value: string | null) => value === null || value.trim() === '';

/**
 * 🔒 The boxes a row opens with (decision 3084; the owner, 2026-10-11:
 * *"default all checks on the here now unless blank and the other has data"*):
 * a field is ticked where the value here is blank and the incoming one carries
 * data, and a locked field is ticked. Only a pickable field has a box.
 */
export function acceptTicksOf(row: Pick<StagedRowView, 'sourceKind' | 'matched' | 'matchSetAside' | 'fields'>): Record<string, boolean> {
  const hasRecord = acceptHasRecord(row);
  return Object.fromEntries(row.fields.filter((field) => field.pickable).map((field) => [field.field, acceptLocked(row.sourceKind, hasRecord, field.field) || (blankValue(field.here) && !blankValue(field.converted))]));
}

/** 🔒 The Accept column's word for one field (decision 3084). */
export function acceptWordOf(field: Pick<StagedFieldView, 'differs' | 'converted'>, hasRecord: boolean, ticked: boolean): AcceptWord {
  if (hasRecord && !field.differs) return 'Same';
  if (!ticked || (!hasRecord && blankValue(field.converted))) return 'Keep';
  return hasRecord ? 'Update' : 'Create';
}

/**
 * 🔒 Decision 7940 (T3182; the owner, 2026-10-26: *"associate only add the link of employment or site"*): a contact row a
 * rule matched, to a company here or to one a sibling row of the same read creates, is decided as an Associate. It links
 * the contact, adds its addresses as sites, employs its people there and adds a flag the company lacks, and writes no
 * other field of the company. A row an earlier pull linked carries no rule and stays an Update.
 */
export function associatesRow(row: { sourceKind: string; matchRuleId: string | null; matchSetAside: boolean; proposedOutcome: string }): boolean {
  return row.sourceKind === 'Contact' && row.matchRuleId !== null && !row.matchSetAside && row.proposedOutcome === 'Update';
}

/** 🔒 Why an Accept naming fields is refused on an Associate row (decision 7940): it writes none, and Merge is the act that picks them. */
export const ASSOCIATE_NO_FIELDS = 'This row associates with the company and writes none of its fields; Merge picks the fields to write.';

/** 🔒 The two addresses a Zoho contact carries, which an Associate adds as sites (decision 7940). */
export const ASSOCIATE_ADDRESS_ROLES = ['billing', 'shipping'] as const;
export type AssociateAddressRole = (typeof ASSOCIATE_ADDRESS_ROLES)[number];
/** 🔒 What an Edit on an Associate row types of a site and of a person (decision 7940). */
export const ASSOCIATE_ADDRESS_FIELDS = ['line1', 'city', 'state', 'postalCode'] as const;
export type AssociateAddressField = (typeof ASSOCIATE_ADDRESS_FIELDS)[number];
export const ASSOCIATE_PERSON_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'department'] as const;
export type AssociatePersonField = (typeof ASSOCIATE_PERSON_FIELDS)[number];

/** The `editedValues` key of an address's field on an Associate row. */
export function associateAddressKey(role: AssociateAddressRole, field: AssociateAddressField): string {
  return `address.${role}.${field}`;
}
/** The `editedValues` key of a Zoho contact person's field on an Associate row. */
export function associatePersonKey(contactPersonId: string, field: AssociatePersonField): string {
  return `person.${contactPersonId}.${field}`;
}

type AssociateSource = Pick<BooksContact, 'billingAddress' | 'shippingAddress' | 'persons'>;
const addressOfRole = (contact: AssociateSource, role: AssociateAddressRole): BooksAddress | null => (role === 'billing' ? contact.billingAddress : contact.shippingAddress) ?? null;

/** 🔒 What one Edit key names on the contact, or null where it names no address or person the contact carries (decision 7940). */
function associateTarget(contact: AssociateSource, key: string): { kind: 'address'; role: AssociateAddressRole; field: AssociateAddressField } | { kind: 'person'; person: BooksContactPerson; field: AssociatePersonField } | null {
  const [head, middle, field, ...rest] = key.split('.');
  if (rest.length > 0 || !middle || !field) return null;
  if (head === 'address' && (ASSOCIATE_ADDRESS_ROLES as readonly string[]).includes(middle) && (ASSOCIATE_ADDRESS_FIELDS as readonly string[]).includes(field)) {
    return addressOfRole(contact, middle as AssociateAddressRole) ? { kind: 'address', role: middle as AssociateAddressRole, field: field as AssociateAddressField } : null;
  }
  if (head === 'person' && (ASSOCIATE_PERSON_FIELDS as readonly string[]).includes(field)) {
    const person = (contact.persons ?? []).find((one) => one.id === middle);
    return person ? { kind: 'person', person, field: field as AssociatePersonField } : null;
  }
  return null;
}

/** 🔒 Whether an Edit key names a site or a person this contact's Associate adds (decision 7940). */
export function isAssociateEditKey(contact: AssociateSource, key: string): boolean {
  return associateTarget(contact, key) !== null;
}

/** 🔒 The Zoho value an Edit key names, before anything typed over it (decision 7940); null where it names nothing. */
export function associateEditValue(contact: AssociateSource, key: string): string | null {
  const target = associateTarget(contact, key);
  if (!target) return null;
  if (target.kind === 'person') return target.person[target.field] || null;
  const address = addressOfRole(contact, target.role)!;
  return (target.field === 'line1' ? address.lines[0] : address[target.field]) || null;
}

/**
 * 🔒 The contact with an Associate's typed site and person values over Zoho's (decision 7940): a copy, so the content hash
 * the company's reference keeps is still taken from Zoho's own values. A key naming nothing on the contact is passed over.
 */
export function withAssociateEdits<T extends AssociateSource>(contact: T, edits: Readonly<Record<string, unknown>>): T {
  const keys = Object.keys(edits).filter((key) => isAssociateEditKey(contact, key));
  if (keys.length === 0) return contact;
  const copy = { ...contact, billingAddress: contact.billingAddress ? { ...contact.billingAddress, lines: [...contact.billingAddress.lines] } : contact.billingAddress, shippingAddress: contact.shippingAddress ? { ...contact.shippingAddress, lines: [...contact.shippingAddress.lines] } : contact.shippingAddress, persons: (contact.persons ?? []).map((one) => ({ ...one })) } as T;
  for (const key of keys) {
    const raw = edits[key];
    const text = typeof raw === 'string' ? raw.trim() : '';
    const target = associateTarget(copy, key)!;
    if (target.kind === 'person') {
      if (target.field === 'firstName' || target.field === 'lastName') target.person[target.field] = text;
      else target.person[target.field] = text || null;
      continue;
    }
    const address = addressOfRole(copy, target.role)!;
    if (target.field === 'line1') address.lines = [text, ...address.lines.slice(1)].filter((one, index) => index > 0 || one !== '');
    else address[target.field] = text || null;
  }
  return copy;
}

/** 🔒 One site an Associate adds (decision 7940): the address's role and its fields, typed values over Zoho's. */
export interface StagedAssociationSite {
  role: AssociateAddressRole;
  line1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

/** 🔒 One person an Associate employs at the company (decision 7940), typed values over Zoho's. */
export interface StagedAssociationPerson {
  contactPersonId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  /** 🔒 Decision 8100: the person's department text, typed over Zoho's; null where none, and absent on a row read before. */
  department?: string | null;
}

/** 🔒 What an Associate row reads (decision 7940): the company it associates with, and the sites and people it adds. */
export interface StagedAssociation {
  into: { label: string; recordId: string | null; route: string | null; plannedByRun: boolean };
  sites: StagedAssociationSite[];
  people: StagedAssociationPerson[];
}

/** 🔒 The sites and people an Associate adds, read off the contact with its typed values (decision 7940); a shipping address at the billing one's place adds no second site. */
export function associationAdds(contact: AssociateSource, edits: Readonly<Record<string, unknown>> = {}): Pick<StagedAssociation, 'sites' | 'people'> {
  const typed = withAssociateEdits(contact, edits);
  const sites: StagedAssociationSite[] = [];
  for (const role of ASSOCIATE_ADDRESS_ROLES) {
    const address = addressOfRole(typed, role);
    if (!address) continue;
    const site: StagedAssociationSite = { role, line1: address.lines[0] || null, city: address.city, state: address.state, postalCode: address.postalCode };
    const place = (one: StagedAssociationSite) => `${(one.line1 ?? '').trim().toLowerCase()}|${(one.postalCode ?? '').trim()}`;
    if (!sites.some((one) => place(one) === place(site))) sites.push(site);
  }
  const people = (typed.persons ?? []).map((one) => ({ contactPersonId: one.id, firstName: one.firstName, lastName: one.lastName, email: one.email, phone: one.phone, department: one.department ?? null }));
  return { sites, people };
}

/** A site as an Associate's notes line names it: its street line, or its city where it carries none. */
export function associationSiteText(site: StagedAssociationSite): string {
  return site.line1 ?? site.city ?? site.postalCode ?? site.role;
}

/** A person as an Associate's notes line names them: their name, or their email where Zoho gives none. */
export function associationPersonText(person: StagedAssociationPerson): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email || person.contactPersonId;
}

/** A staged row as the list reads it. */
export interface StagedRowSummary {
  id: string;
  sequence: number;
  sourceKind: ImportStagedKindValue;
  sourceId: string;
  sourceLabel: string;
  matchGrade: ImportMatchGradeValue;
  proposedOutcome: ImportStagedOutcomeValue;
  changedHere: boolean;
  decision: ImportStagedDecisionValue | null;
  appliedAt: string | null;
  applyRefusal: string | null;
  decidedFromRowId: string | null;
  revisitedAt: string | null;
  shapeKey: string;
  /** A contact row whose read noted a record of another name at the same street address (decisions 1625, 1880). */
  possibleDuplicate?: boolean;
  /** 🔒 A contact row one of whose people another contact's person might be (decision 2502). */
  samePerson?: boolean;
  /** 🔒 One of those pairs has no answer yet, so the row's Accept waits (decision 2970). */
  samePersonWaiting?: boolean;
  /** 🔒 Decision 7940: a contact row a rule matched, decided as an Associate; absent on every other row. */
  associates?: boolean;
  /** 🔒 A Reject row's reason as the importer wrote it, which the list's Proposed column shows on hover (decision 12911; T3866); absent on every other row. */
  proposedReason?: string | null;
}

/** A staged row as the step-through reads it: the summary, the match, the compare and the offers. */
export interface StagedRowView extends StagedRowSummary {
  matched: { recordType: ImportMatchedRecordTypeValue; recordId: string; label: string; route: string | null } | null;
  matchKey: ImportKeyKindValue | null;
  matchReason: string | null;
  matchSetAside: boolean;
  /** 🔒 The match is the link an earlier pull left, not a rule's proposal (decision 7410); absent where it is not. */
  linked?: boolean;
  proposedReason: string | null;
  /** 🔒 Decision 8160: the sibling row whose converted values the Here Now column shows, on a join to the company it creates; absent on every other row. */
  hereFrom?: { rowId: string; sequence: number; label: string };
  fields: StagedFieldView[];
  /** The conversion's notes naming no one field. */
  notes: string[];
  /** The unit string that matched no Unit, where the row waits on a match. */
  unitText: string | null;
  /** 🔒 An item's details as the whole read kept them (decision 1676), each a label and a value; empty on every other kind. */
  details?: { label: string; value: string }[];
  merged: { recordType: ImportMergeTargetTypeValue; recordId: string; label: string } | null;
  fieldChoices: string[];
  decidedBy: string | null;
  decidedAt: string | null;
  decidedFrom: { id: string; sequence: number; sourceLabel: string } | null;
  offers: StagedOffers;
  /**
   * 🔒 Join instead (decision 1880): an undecided contact row proposing a create
   * at a street address another company's record holds names that company. A
   * Join is a Merge into it with no field taken; `waiting` is the refusal while
   * the company is not written yet.
   */
  join?: { recordId: string | null; label: string; waiting: string | null } | null;
  /** 🔒 The contact's one-name people (decision 2500): each asked about because the matcher could not place them, answered or waiting. */
  oneName?: OneNameQuestion[];
  /** 🔒 The contact's possible pairs with another contact's people (decision 2502), each offered Merge and Keep both. */
  samePersonPairs?: SamePersonPair[];
  /** 🔒 The opening row's accounts no pull linked (decision 2030; T1873), which Step through places with Match and Create as the rejected list does; absent on every other kind. */
  unlinked?: OpeningUnlinkedAccount[];
  /** 🔒 A document's lines naming an item no pull linked, and the three acts its row offers (decisions 4020, 4801); absent where no line names a Zoho item. */
  waitingItems?: WaitingItemsView;
  /** 🔒 Decision 5382: a contacts row's validation study, its Suggested column and its people's lines; absent on every other row. */
  study?: StagedStudyView;
  /** 🔒 A credit note's lines matching no line of its invoice, with the invoice's lines to pick from (decision 7350); absent on every other row and where every credit line matches. */
  creditLines?: CreditLinesView;
  /** 🔒 Decision 7500 (U2b): the record this row matches absorbed this row's own record in a merge made before the run, which Undo Merge can take apart; absent where it did not. */
  mergedEarlier?: StagedMergedEarlier;
  /** 🔒 Decision 7940: the company an Associate row associates with, and the sites and people it adds; absent on every other row. */
  association?: StagedAssociation;
}

/** 🔒 A merge made before the run that absorbed the company a contact row came from (decision 7500). */
export interface StagedMergedEarlier {
  mergeId: string;
  /** The merged record's name as the merge recorded it. */
  mergedName: string;
  intoName: string;
  /** The merge's instant, which the line draws as the viewer's day. */
  mergedAt: string;
}

/** 🔒 One line of the credited invoice, as Pick the Invoice Line lists it (decision 7350). */
export interface CreditInvoiceLine {
  id: string;
  lineNumber: number;
  /** The invoice line's item name or its words. */
  label: string;
  quantity: number;
  /** The quantity left after the credit notes already written against the invoice. */
  left: number;
}

/** 🔒 One credit line of the Zoho credit note, and the invoice line it credits where it matches or was picked (decision 7350). */
export interface CreditLineChoice {
  /** The line's position in the Zoho document, which a pick names. */
  index: number;
  label: string;
  quantity: number;
  /** The invoice line it credits, matched or picked; null where it matches none. */
  invoiceLineId: string | null;
  /** True where a person picked the invoice line. */
  picked: boolean;
  /** Who picked it and when, where a person did. */
  by: string | null;
  at: string | null;
  /** Why the line credits no invoice line, or null where it does. */
  problem: string | null;
  /** True where the line matches nothing and a pick can answer it. */
  pickable: boolean;
}

/** 🔒 Pick the Invoice Line on a credit note row (decision 7350). */
export interface CreditLinesView {
  /** The credited invoice here, or null where it is not written here yet. */
  invoice: { number: string | null; route: string | null; lines: CreditInvoiceLine[] } | null;
  /** The credited invoice's row in a run that stages it but has not written it, which the row links to where the invoice is not here yet. */
  invoiceRow: { runId: string; rowId: string; sequence: number; label: string } | null;
  lines: CreditLineChoice[];
}

/**
 * 🔒 The three acts a document row waiting on an item offers (decisions 4020, 4801): Create the Item Here, Match to an Item Here and Make It a Text Line;
 * and the credit note's Pick the Invoice Line (decision 7350), kept beside them as a `CreditLine` act.
 */
export const WAITING_ITEM_ACTS = ['CreateItem', 'MatchItem', 'TextLine', 'CreditLine'] as const;
export type WaitingItemActKind = (typeof WAITING_ITEM_ACTS)[number];

/** 🔒 One act as the document's staged row keeps it (decision 4020): the act, the Zoho item, who took it and when. */
export interface WaitingItemAct {
  act: WaitingItemActKind;
  itemSourceId: string;
  itemName: string | null;
  personId: string;
  at: string;
  /** 🔒 Who pressed Undo the Text Lines on a text line, and when; an undone text line converts as the item's line again, and the act stays on the row. */
  undoneByPersonId?: string;
  undoneAt?: string;
  /** 🔒 Match to an Item Here: the item here the person picked (decision 4801). */
  itemId?: string;
  /** 🔒 An act this row took from another row's choice, remembered for the run: that row's id (decision 4801). */
  followedRowId?: string;
  /** 🔒 Pick the Invoice Line (decision 7350): the credit line's position in the Zoho document, and the id of the invoice line here a person picked for it. */
  creditLineIndex?: number;
  invoiceLineId?: string;
}

/** 🔒 The Zoho items a row's standing text lines name (decision 4020): Make It a Text Line acts not undone. */
export function standingTextLines(acts: readonly WaitingItemAct[]): WaitingItemAct[] {
  return acts.filter((one) => one.act === 'TextLine' && !one.undoneAt);
}

/** 🔒 What a document row waiting on an item offers (decision 4020). */
export interface WaitingItemsView {
  /** The first waiting line's item, which the reason names, or null where no line waits. */
  waiting: {
    itemSourceId: string;
    itemName: string | null;
    /** The Items read's row for it, or null where no read listed it. */
    sequence: number | null;
    /** 🔒 Create the Item Here is drawn only while the item's row is undecided; a skipped or refused row keeps the pointer alone. */
    createShown: boolean;
    /** Why Create the Item Here is shut, or null where it is open. */
    create: string | null;
    /** 🔒 The Items read's run and row for the item, which Match to an Item Here searches the catalog through (decision 4801); null where no read listed it. */
    itemRunId: string | null;
    itemRowId: string | null;
    /** 🔒 Match to an Item Here is drawn while the item's row is undecided, as Create is (decision 4801). */
    matchShown: boolean;
    /** 🔒 The item's row stands in an Items read that is closed (decision 7350), so Read Items Again is drawn on this row. */
    itemsReadClosed: boolean;
    /** Why Match to an Item Here is shut, or null where it is open; a row matched to an item here leaves it open. */
    match: string | null;
  } | null;
  /** How many of the document's items wait; Make It a Text Line takes the first, which the reason names (decision 4801). */
  waitingLines: number;
  /** Why Make It a Text Line and Undo the Text Lines are shut on this row, or null where they are open. */
  shut: string | null;
  /** The lines this row writes as text lines, by the Zoho item each names, with who chose it and when. */
  textLines: { itemSourceId: string; itemName: string | null; by: string | null; at: string }[];
  /** 🔒 The choices made on this row that other rows of the run took, by item, with how many rows took each (decision 4801). */
  followers: { act: WaitingItemActKind; itemSourceId: string; itemName: string | null; rows: number }[];
  /** 🔒 The choices this row took from another row of the run, by item, naming that row (decision 4801). */
  followed: { act: WaitingItemActKind; itemSourceId: string; itemName: string | null; sequence: number | null }[];
}

export interface StagedRowsPage {
  run: ImportRunView;
  rows: StagedRowSummary[];
  /** The rows the filter holds, the undecided rows, and every row the run staged. */
  total: number;
  undecided: number;
  staged: number;
  /** 🔒 The rows writing a line as a text line (decision 4020), counted beside the undecided rows. */
  textLined: number;
}

/** A record a Merge can write into, found by what a person typed. */
export interface MergeCandidate {
  recordType: ImportMergeTargetTypeValue;
  recordId: string;
  label: string;
}

/** A decision a person sends for one row. */
export interface StagedDecisionBody {
  decision: ImportStagedDecisionValue;
  /** Accept or Skip every undecided row of the same shape in this run. */
  remember?: boolean;
  /** Edit: the values typed over the converted ones, by field. */
  editedValues?: Record<string, string | number | null>;
  /** Merge: the record here the row writes into. */
  mergeTarget?: { recordType: ImportMergeTargetTypeValue; recordId: string };
  /** Merge: the fields whose incoming value the person chose; every other field keeps the value here. An Accept from Step through names the fields ticked, and writes those alone (decision 3084). */
  fieldChoices?: string[];
  /** 🔒 Decision 5631: an Accept's ticked suggestions, by their mark keys; each is written after the row through the tile's own Accept. */
  suggestionChoices?: string[];
}

export interface StagedDecisionAnswer {
  row: StagedRowView;
  /** The rows a remembered decision decided with it. */
  followed: number;
  /** 🔒 The rows of the same shape a remembered Accept passed over because a same-person pair waits (decision 2970). */
  pairWaiting?: number;
  /** 🔒 The rows of the same shape a remembered Accept passed over because their proposed match is Weak with differing names (decision 7880). */
  namesDiffer?: number;
  /** 🔒 The rows of the same shape a remembered decision passed over because their company pair waits on its answer (decision 7980). */
  companyPairWaiting?: number;
}

export function stagedRowsPath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/staged`;
}
export function stagedRowPath(runId: string, rowId: string): string {
  return `${stagedRowsPath(runId)}/${encodeURIComponent(rowId)}`;
}
export function stagedDecidePath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/decide`;
}
export function stagedSetAsidePath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/set-aside`;
}
export function stagedReopenPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/reopen`;
}
export function stagedCandidatesPath(runId: string, rowId: string, query: string): string {
  return `${stagedRowPath(runId, rowId)}/candidates?q=${encodeURIComponent(query)}`;
}
/** 🔒 A one-name question's answer on a contact row (decision 2500). */
export function stagedContactPersonPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/contact-person`;
}
/** 🔒 The people here a one-name question's Pick can name (decision 2500). */
export function stagedPersonCandidatesPath(runId: string, rowId: string, query: string): string {
  return `${stagedRowPath(runId, rowId)}/person-candidates?q=${encodeURIComponent(query)}`;
}
/** 🔒 Create the Item Here on a document row waiting on an item (decision 4020); answers the document's row read again. */
export function stagedCreateItemPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/create-item`;
}
/** 🔒 Match to an Item Here on a document row waiting on an item (decision 4801), with `{ itemId }`; answers the document's row read again. */
export function stagedMatchItemPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/match-item`;
}
/** 🔒 Pick the Invoice Line on a credit note row (decision 7350), with `{ lineIndex, invoiceLineId }`; answers the credit note's row read again. */
export function stagedCreditLinePath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/credit-line`;
}
/** 🔒 Make It a Text Line on a document row waiting on an item (decisions 4020, 4801), for the item its reason names. */
export function stagedTextLinesPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/text-lines`;
}
/** 🔒 Undo the Text Lines (decision 4020): the document's lines wait on their items again. */
export function stagedTextLinesUndoPath(runId: string, rowId: string): string {
  return `${stagedTextLinesPath(runId, rowId)}/undo`;
}
export function stagedBulkPath(runId: string): string {
  return `${stagedRowsPath(runId)}/bulk`;
}
export function importRunConvertAgainPath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/convert-again`;
}
export function importRunClosePath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/close`;
}
/** 🔒 Write all (decision 1483): every undecided row the read listed, written from the rows already read. */
export function importRunWriteAllPath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/write-all`;
}
/** 🔒 Refresh (decision 1483): the scope read again into new rows, the undecided old ones retired. */
export function importRunRefreshPath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/refresh`;
}

// ---- The three passes of a Contacts read (decision 7980) ----

/** 🔒 The key a three-pass read keeps its answers under in the run's `contactPersonAnswers` (decision 7980); a Zoho id is never this word. */
export const IMPORT_PASSES_KEY = 'passes';

/** Who gave an answer of the passes and when. */
export interface ImportPassAnswered {
  answeredByPersonId: string;
  answeredAt: string;
}

/**
 * 🔒 A three-pass read's answers (decision 7980): the Zoho contacts joined in the
 * Companies pass, those kept apart with the company they were kept apart from,
 * and the People pass's Continue. Its presence marks the read as three-pass.
 */
export interface ImportPassesAnswer {
  kind: 'Passes';
  joined: Record<string, ImportPassAnswered>;
  keptApart: Record<string, ImportPassAnswered & { from: string }>;
  peopleContinued?: ImportPassAnswered;
}

/** 🔒 A new three-pass read's answers (decision 7980): nothing joined, nothing kept apart, the People pass open. */
export function emptyPassesAnswer(): ImportPassesAnswer {
  return { kind: 'Passes', joined: {}, keptApart: {} };
}

/** 🔒 The passes a run's answers hold (decision 7980), or null for a read opened before it, which keeps the one walk. */
export function passesAnswerOf(answers: unknown): ImportPassesAnswer | null {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return null;
  const held = (answers as Record<string, unknown>)[IMPORT_PASSES_KEY] as Partial<ImportPassesAnswer> | undefined;
  if (!held || held.kind !== 'Passes') return null;
  return { kind: 'Passes', joined: { ...(held.joined ?? {}) }, keptApart: { ...(held.keptApart ?? {}) }, ...(held.peopleContinued ? { peopleContinued: held.peopleContinued } : {}) };
}

/** 🔒 Whether two company names differ once folded as the name rule folds them (decisions 1880, 7980). */
export function foldedNamesDiffer(one: string, other: string): boolean {
  return normaliseCompanyName(one) !== normaliseCompanyName(other);
}

/** 🔒 A pair's answer in the Companies pass (decision 7980): Join keeps the proposal, Keep Apart is Not This One. */
export const COMPANY_PAIR_ANSWERS = ['Join', 'KeepApart'] as const;
export type CompanyPairAnswer = (typeof COMPANY_PAIR_ANSWERS)[number];

/** 🔒 One line of the Companies pass (decision 7980): the row's company, the company it joins, the rule and the reason, and the answer. */
export interface CompanyPairView {
  rowId: string;
  sequence: number;
  /** The row's own company, as the read names it. */
  label: string;
  /** The company the row joins: one here with its page, or one a sibling row of the read creates. */
  into: { label: string; recordId: string | null; route: string | null; plannedByRun: boolean };
  ruleKey: ImportKeyKindValue | null;
  grade: ImportMatchGradeValue;
  reason: string | null;
  /** The two names differ once folded, which the line notes and the list puts first. */
  namesDiffer: boolean;
  answer: CompanyPairAnswer | null;
  /** The row is decided, so its answer is read and not changed here. */
  decided: boolean;
  /** The row was skipped, so the list says Skipped and not Written (T3213). */
  skipped?: boolean;
}

/** 🔒 The Companies pass's order (decision 7980): the pairs whose names differ first, then the rest, each in the read's order. */
export function companyPairsInOrder<T extends Pick<CompanyPairView, 'namesDiffer' | 'sequence'>>(pairs: readonly T[]): T[] {
  return [...pairs].sort((a, b) => Number(b.namesDiffer) - Number(a.namesDiffer) || a.sequence - b.sequence);
}

/** 🔒 One line of the People pass (decision 7980): a one-name question or a same-person pair, with the row it stands on. */
export type PeopleQuestionView = { rowId: string; sequence: number; company: string; answered: boolean } & (
  | { kind: 'OneName'; oneName: OneNameQuestion }
  | { kind: 'SamePerson'; pair: SamePersonPair }
);

/** 🔒 A one-name question waits until it is answered, unless the read proposed both names off the email (decisions 2500, 6330). */
export function oneNameWaits(question: Pick<OneNameQuestion, 'answer' | 'proposed'>): boolean {
  return question.answer === null && !(question.proposed && question.proposed.firstName && question.proposed.lastName);
}

/** 🔒 What Step through reads of a three-pass read (decision 7980); `passes` is null on a read that keeps the one walk. */
export interface ImportPassesView {
  passes: (ImportPassesSummary & { companies: CompanyPairView[]; people: PeopleQuestionView[] }) | null;
}

/** 🔒 The three-pass read's own reads and acts (decision 7980). */
export function importRunPassesPath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/passes`;
}
/** 🔒 Continue, with `{ pass }`: Companies leaves every unanswered pair Keep Apart, People moves on with its questions left. */
export function importRunPassContinuePath(runId: string): string {
  return `${importRunPassesPath(runId)}/continue`;
}
export function stagedJoinPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/join`;
}
export function stagedKeepApartPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/keep-apart`;
}

/** 🔒 How a person places an account the opening row lists (decision 2030): matched to an account here, or created as block 1's Accept would. */
export const OPENING_PLACE_ACTIONS = ['Match', 'Create'] as const;
export type OpeningPlaceAction = (typeof OPENING_PLACE_ACTIONS)[number];

/** One unlinked account placed: its Zoho id, the action, and for a Match the account here. */
export interface OpeningPlaceBody {
  sourceAccountId: string;
  action: OpeningPlaceAction;
  accountId?: string;
}

/** 🔒 An unlinked account of a Staged documents run's opening row placed (decision 2030); answers the run as it then stands. */
export function importRunOpeningPlacePath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/opening/place`;
}
/** 🔒 The accounts here a Match on the opening row can link to, found by name or number (decision 2030). */
export function importRunOpeningCandidatesPath(runId: string, query: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/opening/candidates?q=${encodeURIComponent(query)}`;
}

// ---- The cleanup pass (decision 1055) ----

/**
 * Why a row stands on a closed run's cleanup list; a placeholder site a write opened stands after the undecided rows
 * (decision 4802). 🔒 An invoice held for its tax stands first (decision 11661), because it waits on a person's act.
 */
export const CLEANUP_CATEGORIES = ['TaxHold', 'Weak', 'Edited', 'Merged', 'Skipped', 'Undecided', 'PlaceholderSite'] as const;
export type CleanupCategory = (typeof CLEANUP_CATEGORIES)[number];

/**
 * 🔒 Where a closed Stepped run's row stands on its cleanup list, or null for
 * a row it left certain: a Weak match first, then a row accepted with edits,
 * merged, skipped, and never decided or refused when written. A written row
 * whose write opened a placeholder site stands last (decision 4802).
 */
export function cleanupCategoryOf(row: { matchGrade: ImportMatchGradeValue; decision: ImportStagedDecisionValue | null; appliedAt: string | null; applyRefusal: string | null }, openedPlaceholder = false, taxHeld = false): CleanupCategory | null {
  // 🔒 Decision 11661: a written invoice held for its tax, until a person keeps the calculation or uses Zoho Books' tax.
  if (taxHeld && row.appliedAt) return 'TaxHold';
  if (row.matchGrade === 'Weak') return 'Weak';
  if (row.decision === 'Edit' && row.appliedAt) return 'Edited';
  if (row.decision === 'Merge' && row.appliedAt) return 'Merged';
  if (row.decision === 'Skip') return 'Skipped';
  if (row.decision === null || row.applyRefusal) return 'Undecided';
  if (openedPlaceholder && row.appliedAt) return 'PlaceholderSite';
  return null;
}

/** One entry of the cleanup list: the row, why it stands there, and the two companies a set-aside Weak match left apart. */
export interface CleanupEntry {
  rowId: string;
  sequence: number;
  sourceKind: ImportStagedKindValue;
  sourceId: string;
  sourceLabel: string;
  category: CleanupCategory;
  matchGrade: ImportMatchGradeValue;
  decision: ImportStagedDecisionValue | null;
  applyRefusal: string | null;
  revisitedAt: string | null;
  /** 🔒 Why nothing was written for a row the read itself rejected (decision 3262); null on every other row, whose refusal is `applyRefusal`. */
  rejectedReason?: string | null;
  /** 🔒 The earlier block's row a document waits on (decision 3262), which Revisit opens instead of reading the document again. */
  waitsOnRow?: ImportWaitsOnRow | null;
  /** 🔒 The placeholder sites the row's write opened, each with its company and the company's page, where the real address is added (decision 4802). */
  placeholderSites?: (PlaceholderSiteOpened & { route: string })[];
  /** 🔒 An invoice held for its tax (decision 11661): our amount, Zoho Books', and the invoice's page, where neither act is chosen yet. */
  taxHold?: { invoiceId: string; computedCents: number; zohoCents: number; route: string };
  /** A Weak company match set aside into two companies: the one here it matched, and the one the Accept created. */
  pair: { matched: { id: string; label: string; route: string }; created: { id: string; label: string; route: string } | null } | null;
}

export interface CleanupView {
  runId: string;
  counts: Record<CleanupCategory, number>;
  /** Weak matches first, then edited, merged, skipped and undecided; a revisited entry after every other. */
  entries: CleanupEntry[];
}

export function importRunCleanupPath(runId: string): string {
  return `/import/runs/${encodeURIComponent(runId)}/cleanup`;
}
export function stagedRevisitPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/revisit`;
}
/** 🔒 Keep the Calculation on a held invoice's row (decision 11661): issues it with the tax our rates worked out; answers the cleanup list. */
export function stagedTaxKeepPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/tax/keep`;
}
/** 🔒 Use Zoho's Tax on a held invoice's row (decision 11661): issues it with Zoho Books' tax amount; answers the cleanup list. */
export function stagedTaxZohoPath(runId: string, rowId: string): string {
  return `${stagedRowPath(runId, rowId)}/tax/zoho`;
}
